import React from 'react';
import { Card, Row, Col } from 'antd';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const CoursesByCategoryCompactChart = ({ data }) => {
  // Helper function to safely get data from different possible structures
  const getCoursesByCategory = () => {
    if (!data) return [];
    
    // Try different possible data structures
    const paths = [
      'coursesByCategory',
      'data.coursesByCategory',
      'overview.coursesByCategory',
      'data.overview.coursesByCategory'
    ];
    
    for (const path of paths) {
      const value = path.split('.').reduce((obj, key) => obj?.[key], data);
      if (value && Array.isArray(value) && value.length > 0) {
        return value;
      }
    }
    
    return [];
  };

  const coursesByCategory = getCoursesByCategory();

  if (coursesByCategory.length === 0) {
    return (
      <Card title="Courses by Category" className="chart-card">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ color: '#999', padding: '40px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
            <p>No category data available yet</p>
            <p style={{ fontSize: '12px' }}>Category data will appear here when courses are created</p>
          </div>
        </div>
      </Card>
    );
  }

  const chartData = coursesByCategory
    .sort((a, b) => b.courseCount - a.courseCount)
    .map((category, index) => ({
      name: category.categoryName,
      value: category.courseCount,
      id: category._id,
      slug: category.categorySlug,
      isOther: false
    }));

  const COLORS = [
    '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
    '#82CA9D', '#FFC658', '#FF6B6B', '#4ECDC4', '#45B7D1'
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>
            {data.name}
          </p>
          <p style={{ margin: 0, color: '#666' }}>
            Courses: {data.value}
          </p>
          <p style={{ margin: 0, color: '#999' }}>
            {((data.value / chartData.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card title="Courses by Category" className="chart-card">
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '350px' }}>
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={false}
                  outerRadius={80}
                  innerRadius={40}
                  fill="#8884d8"
                  dataKey="value"
                  paddingAngle={1}
                >
                  {chartData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={COLORS[index % COLORS.length]} 
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Col>
        
        <Col xs={24} lg={16}>
          <h4 style={{ marginBottom: '16px', color: '#333' }}>All Categories ({chartData.length})</h4>
          <div className="scrollable-area">
                <Row gutter={[8, 4]}>
                  {chartData.map((item, index) => (
                    <Col xs={24} sm={12} key={index}>
                      <div 
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '6px 8px',
                          borderRadius: '4px',
                          backgroundColor: index % 2 === 0 ? '#fafafa' : 'transparent',
                          marginBottom: '2px'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                          <div 
                            style={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '2px',
                              backgroundColor: COLORS[index % COLORS.length],
                              marginRight: '8px',
                              flexShrink: 0
                            }}
                          />
                          <span style={{ 
                            fontSize: '12px',
                            color: '#333',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {item.name}
                          </span>
                        </div>
                        <div style={{ textAlign: 'right', marginLeft: '8px' }}>
                          <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#333' }}>
                            {item.value}
                          </div>
                          <div style={{ fontSize: '10px', color: '#666' }}>
                            {((item.value / chartData.reduce((sum, i) => sum + i.value, 0)) * 100).toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            </Col>
          </Row>
        </Card>
      );
};

export default CoursesByCategoryCompactChart; 